import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import apiClient from "../utils/api";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
    />
  </svg>
);

const MailIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const EasyChatLogo = () => (
  <img
    src="/easychat-logo.png"
    alt="EasyChat - Where AI Meets Work"
    style={{
      height: "52px",
      width: "auto",
      maxWidth: "210px",
      objectFit: "contain",
      mixBlendMode: "multiply",
      display: "block",
    }}
  />
);

const Login = ({ onLogin }) => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => {
      setError("");
    }, 3000);
    return () => clearTimeout(timer);
  }, [error]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await apiClient.request("/auth/login", {
        method: "POST",
        body: JSON.stringify(credentials),
        skipToast: true,
      });

      const userData = {
        authenticated: true,
        token: response.access_token,
        role: response.role,
        username: response.username,
        id: response.id,
        human_agent_id: response.human_agent_id,
        ai_agent_id: response.ai_agent_id,
        tenant_id: response.tenant_id,
        assigned_ai_agents: response.assigned_ai_agents || [],
      };
      sessionStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("user", JSON.stringify(userData));

      if (response.ai_agent_id && response.ai_agent_id !== "all") {
        apiClient.setAiAgentId(response.ai_agent_id);
        localStorage.setItem("activeAiAgentId", response.ai_agent_id);
      }
      if (response.tenant_id) {
        localStorage.setItem("activeTenantId", response.tenant_id);
      }

      onLogin();
      navigate("/admin");
    } catch (err) {
      const errMsg = err?.detail || err?.message || "Invalid credentials. Please try again.";
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
        }
        body {
          margin: 0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          background-color: #ffffff;
          overflow: hidden;
        }
        .page-container {
          display: flex;
          min-height: 100vh;
          width: 100vw;
          background: radial-gradient(circle at 12% 25%, #e0f2fe 0%, #f0fdf4 25%, #faf5ff 55%, #ffffff 80%);
          position: relative;
          overflow: hidden;
        }

        /* Ambient subtle background glow matching hero artwork */
        .page-container::before {
          content: "";
          position: absolute;
          top: -15%;
          left: -10%;
          width: 55%;
          height: 75%;
          background: radial-gradient(circle, rgba(186, 230, 253, 0.6) 0%, rgba(209, 250, 229, 0.35) 45%, transparent 70%);
          filter: blur(70px);
          pointer-events: none;
          z-index: 0;
        }

        .page-container::after {
          content: "";
          position: absolute;
          bottom: -15%;
          left: 5%;
          width: 50%;
          height: 65%;
          background: radial-gradient(circle, rgba(237, 233, 254, 0.5) 0%, rgba(250, 232, 255, 0.3) 50%, transparent 70%);
          filter: blur(80px);
          pointer-events: none;
          z-index: 0;
        }

        /* Left Column: Login Section */
        .left-col {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 40px 32px;
          z-index: 2;
        }

        .login-card {
          width: 100%;
          max-width: 380px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          background: transparent;
          border: none;
          box-shadow: none;
          padding: 0;
        }

        .login-logo-box {
          margin-bottom: 26px;
        }

        .main-headline {
          font-size: 1.72rem;
          font-weight: 700;
          line-height: 1.28;
          color: #111827;
          margin: 0 0 32px 0;
          letter-spacing: -0.025em;
        }

        .actions-stack {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        /* Google button - solid black rounded pill */
        .btn-google {
          width: 100%;
          height: 48px;
          background: #000000;
          color: #ffffff;
          border: 1px solid #000000;
          border-radius: 24px;
          font-size: 0.92rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }

        .btn-google:hover {
          background: #1f2937;
          border-color: #1f2937;
          transform: translateY(-1px);
        }

        /* Continue with email button */
        .btn-email-toggle {
          width: 100%;
          height: 48px;
          background: #ffffff;
          color: #374151;
          border: 1px solid #e5e7eb;
          border-radius: 24px;
          font-size: 0.92rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(0,0,0,0.03);
        }

        .btn-email-toggle:hover {
          background: #f9fafb;
          border-color: #d1d5db;
          color: #111827;
        }

        .btn-email-toggle.active {
          background: #f3f4f6;
          border-color: #cbd5e1;
        }

        /* Collapsible / Animated Form Box */
        .email-form-container {
          width: 100%;
          margin-top: 8px;
          animation: slideDownFade 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          text-align: left;
        }

        @keyframes slideDownFade {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .form-group {
          margin-bottom: 12px;
        }

        .form-label {
          display: block;
          font-size: 0.8rem;
          font-weight: 600;
          color: #4b5563;
          margin-bottom: 5px;
          margin-left: 2px;
        }

        .form-input {
          width: 100%;
          height: 44px;
          padding: 0 14px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          font-size: 0.9rem;
          color: #111827;
          background: #ffffff;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }

        .form-input:focus {
          border-color: #111827;
          box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.08);
        }

        .password-input-wrapper {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
        }

        .password-input {
          padding-right: 44px;
        }

        .password-toggle-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          border-radius: 8px;
          transition: color 0.15s, background-color 0.15s;
        }

        .password-toggle-btn:hover {
          color: #111827;
          background-color: #f3f4f6;
        }

        .password-toggle-btn:focus {
          outline: none;
          color: #111827;
        }

        .btn-submit {
          width: 100%;
          height: 46px;
          background: #111827;
          color: #ffffff;
          border: none;
          border-radius: 23px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: background 0.15s ease, transform 0.15s ease;
          margin-top: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .btn-submit:hover:not(:disabled) {
          background: #000000;
          transform: translateY(-1px);
        }

        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .back-link {
          display: block;
          text-align: center;
          font-size: 0.82rem;
          color: #6b7280;
          background: none;
          border: none;
          cursor: pointer;
          margin: 10px auto 0 auto;
          text-decoration: underline;
          padding: 4px;
        }

        .back-link:hover {
          color: #111827;
        }

        .terms-text {
          font-size: 0.77rem;
          line-height: 1.45;
          color: #6b7280;
          margin-top: 32px;
        }

        .terms-text a {
          color: #111827;
          text-decoration: underline;
          font-weight: 500;
          transition: color 0.15s;
        }

        .terms-text a:hover {
          color: #4b5563;
        }

        .error-alert {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          font-size: 0.82rem;
          padding: 8px 12px;
          border-radius: 8px;
          margin-bottom: 12px;
          text-align: center;
        }

        /* Right Column: Hero Image Showcase seamlessly blended into canvas */
        .right-col {
          flex: 1.25;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          height: 100vh;
          position: relative;
          z-index: 1;
        }

        .hero-banner-wrapper {
          width: 100%;
          height: 100vh;
          background: transparent;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          -webkit-mask-image: linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.35) 3%, rgba(0, 0, 0, 0.85) 7%, black 11%);
          mask-image: linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.35) 3%, rgba(0, 0, 0, 0.85) 7%, black 11%);
        }

        .hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: left center;
          display: block;
        }

        /* Responsive Breakpoints */
        @media (max-width: 960px) {
          .page-container {
            flex-direction: column-reverse;
            overflow-y: auto;
            height: auto;
          }
          body {
            overflow: auto;
          }
          .right-col {
            padding: 0;
            width: 100%;
            height: 380px;
            flex: none;
          }
          .hero-banner-wrapper {
            height: 100%;
            -webkit-mask-image: linear-gradient(to bottom, black 65%, transparent 100%);
            mask-image: linear-gradient(to bottom, black 65%, transparent 100%);
          }
          .left-col {
            padding: 30px 20px 40px 20px;
            flex: none;
            width: 100%;
          }
          .login-card {
            max-width: 380px;
            padding: 0;
          }
        }
      `}</style>

      <div className="page-container">
        {/* Left Login Area */}
        <div className="left-col">
          <div className="login-card">
            <div className="login-logo-box">
              <EasyChatLogo />
            </div>

            <h1 className="main-headline">
              Turn every conversation
              <br />
              into an opportunity
            </h1>

            {error && <div className="error-alert">{error}</div>}

            <div className="actions-stack">
              {/* Google Button */}
              <button
                type="button"
                className="btn-google"
                onClick={() => setShowEmailForm(true)}
              >
                <GoogleIcon />
                <span>Continue with Google</span>
              </button>

              {/* Continue with Email button (hidden when email form is active) */}
              {!showEmailForm && (
                <button
                  type="button"
                  className="btn-email-toggle"
                  onClick={() => setShowEmailForm(true)}
                >
                  <MailIcon />
                  <span>Continue with email</span>
                </button>
              )}

              {/* Email & Password Form (Shown when Continue with Email clicked) */}
              {showEmailForm && (
                <form className="email-form-container" onSubmit={handleLogin}>
                  <div className="form-group">
                    <label className="form-label">Email or Username</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="name@example.com or admin"
                      required
                      autoFocus
                      value={credentials.username}
                      onChange={(e) =>
                        setCredentials({ ...credentials, username: e.target.value })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-input password-input"
                        placeholder="••••••••"
                        required
                        value={credentials.password}
                        onChange={(e) =>
                          setCredentials({ ...credentials, password: e.target.value })
                        }
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={loading}
                  >
                    {loading ? "Signing in..." : "Sign in to EasyChat"}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </button>
                </form>
              )}
            </div>

            <p className="terms-text">
              By continuing, you agree to our
              <br />
              <a href="#terms" onClick={(e) => e.preventDefault()}>Terms of Service</a> and{" "}
              <a href="#privacy" onClick={(e) => e.preventDefault()}>Privacy Policy</a>.
            </p>
          </div>
        </div>

        {/* Right Hero / Showcase Image */}
        <div className="right-col">
          <div className="hero-banner-wrapper">
            <img
              src="/easychat-login-hero.png"
              alt="EasyChat Platform Preview"
              className="hero-image"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;

